class CartDrawer {
    constructor() {
        this.drawer = document.querySelector("[data-cart-drawer]");
        this.backdrop = document.querySelector("[data-cart-drawer-backdrop]");
        this.closeButton = document.querySelector("[data-cart-drawer-close]");
        this.cartContent = document.querySelector("[data-cart-drawer-content]");
        this.cartEmpty = document.querySelector("[data-cart-drawer-empty]");
        this.cartItemsContainer = document.querySelector('[data-section="cart-drawer-items"]');
        this.cartCount = document.querySelectorAll("[data-cart-count]");
        this.cartTotal = document.querySelectorAll("[data-cart-total]");
        if (this.cartItemsContainer) {
            this.cartItemsContainer.addEventListener("click", this.handleCartItemClick.bind(this));
            this.cartItemsContainer.addEventListener("change", this.handleCartItemChange.bind(this));
        } else {
            console.warn("[Cart Drawer] Cart items container not found - event delegation not attached");
        }
        this.isOpen = false;
        this.isUpdating = false;
        this.updateDebounce = null;
        this.focusTrap = null;
        this.lastFocusedElement = null;
        this.animationDuration = .5;
        this.easing = "power2.out";
        this.init();
    }
    init() {
        if (!this.drawer || !this.backdrop) {
            console.warn("Cart drawer elements not found");
            return;
        }
        this.setupInitialStates();
        this.bindEvents();
        this.setupGSAP();
        this.setupCartStateListeners();
        this.checkURLParams();
    }
    setupCartStateListeners() {
        document.addEventListener("cart:updated", e => {
            this.handleCartStateUpdate(e.detail);
            this.refreshCartHTML();
        });
        document.addEventListener("cart:added", e => {
            this.handleCartItemsAdded(e.detail);
        });
        document.addEventListener("cart:error", e => {
            this.handleCartError(e.detail);
        });
    }
    handleCartStateUpdate(detail) {
        const {cart: cart, changes: changes} = detail;
        this.updateCartCountFromState(cart.item_count);
        this.updateCartTotalFromState(cart.total_price);
        this.toggleEmptyState(cart.item_count === 0);
        this.updateCartUI(cart);
    }
    handleCartItemsAdded(detail) {
        this.showSuccess("Added to cart!");
    }
    handleCartError(detail) {
        const {error: error, operation: operation} = detail;
        console.error("[Cart Drawer] Cart error", {
            error: error,
            operation: operation
        });
        this.showError(error.message || "Something went wrong. Please try again.");
    }
    updateCartCountFromState(itemCount) {
        this.cartCount = document.querySelectorAll("[data-cart-count]");
        this.cartCount.forEach(element => {
            element.textContent = itemCount;
            element.setAttribute("data-cart-count", itemCount);
            element.style.display = itemCount > 0 ? "" : "none";
        });
    }
    updateCartTotalFromState(totalPrice) {
        this.cartTotal.forEach(element => {
            element.textContent = this.formatMoney(totalPrice);
        });
    }
    toggleEmptyState(isEmpty) {
        if (this.cartEmpty && this.cartContent) {
            if (isEmpty) {
                this.cartContent.style.display = "none";
                this.cartEmpty.style.display = "block";
            } else {
                this.cartContent.style.display = "block";
                this.cartEmpty.style.display = "none";
            }
        }
    }
    setupInitialStates() {
        gsap.set(this.drawer, {
            x: "100%",
            visibility: "visible",
            display: "block"
        });
        gsap.set(this.backdrop, {
            opacity: 0,
            visibility: "hidden",
            display: "block"
        });
        this.drawer.inert = true;
        this.drawer.setAttribute("aria-hidden", "true");
        this.drawer.setAttribute("role", "dialog");
        this.drawer.setAttribute("aria-label", "Shopping cart");
    }
    setupGSAP() {
        gsap.registerEffect({
            name: "fadeOut",
            effect: (targets, config) => gsap.to(targets, {
                opacity: 0,
                duration: config.duration || .3,
                ease: config.ease || "power2.inOut",
                onComplete: config.onComplete
            }),
            extendTimeline: true
        });
    }
    bindEvents() {
        document.addEventListener("click", e => {
            const trigger = e.target.closest("[data-cart-trigger]");
            if (trigger) {
                e.preventDefault();
                this.openDrawer();
            }
        });
        if (this.closeButton) {
            this.closeButton.addEventListener("click", e => {
                e.preventDefault();
                this.closeDrawer();
            });
        }
        if (this.backdrop) {
            this.backdrop.addEventListener("click", e => {
                if (e.target === this.backdrop) {
                    this.closeDrawer();
                }
            });
        }
        document.addEventListener("keydown", e => {
            if (e.key === "Escape" && this.isOpen) {
                this.closeDrawer();
            }
        });
        document.addEventListener("click", e => {
            const continueBtn = e.target.closest("#continueShoppingBtn");
            if (continueBtn) {
                e.preventDefault();
                this.closeDrawer();
                window.location.href = "/";
            }
        });
        document.addEventListener("cart:add", e => {
            this.handleAddToCart(e.detail);
        });
        document.addEventListener("cart:update", () => {
            this.refreshCart();
        });
    }
    openDrawer() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.lastFocusedElement = document.activeElement;
        const cartDropdown = document.querySelector("[data-cart-dropdown]");
        if (cartDropdown && cartDropdown.classList.contains("is-active")) {
            cartDropdown.classList.remove("is-active");
        }
        this.drawer.inert = false;
        this.drawer.setAttribute("aria-hidden", "false");
        document.body.classList.add("cart-drawer-open");
        const tl = gsap.timeline({
            onStart: () => {
                this.backdrop.style.visibility = "visible";
            },
            onComplete: () => {
                this.trapFocus();
                this.focusFirstElement();
            }
        });
        tl.to(this.backdrop, {
            opacity: .5,
            duration: this.animationDuration,
            ease: this.easing
        }).to(this.drawer, {
            x: "0%",
            duration: this.animationDuration,
            ease: this.easing
        }, "<");
        document.dispatchEvent(new CustomEvent("cart:drawer:opened"));
    }
    closeDrawer() {
        if (!this.isOpen) return;
        this.isOpen = false;
        document.body.classList.remove("cart-drawer-open");
        const tl = gsap.timeline({
            onComplete: () => {
                this.backdrop.style.visibility = "hidden";
                this.releaseFocus();
                if (this.lastFocusedElement) {
                    this.lastFocusedElement.focus();
                }
                this.drawer.inert = true;
                this.drawer.setAttribute("aria-hidden", "true");
            }
        });
        tl.to(this.drawer, {
            x: "100%",
            duration: this.animationDuration,
            ease: "power2.in"
        }).to(this.backdrop, {
            opacity: 0,
            duration: this.animationDuration,
            ease: "power2.in"
        }, "<");
        document.dispatchEvent(new CustomEvent("cart:drawer:closed"));
    }
    checkURLParams() {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("open-cart") === "true") {
            this.openDrawer();
            const url = new URL(window.location.href);
            url.searchParams.delete("open-cart");
            window.history.replaceState({}, "", url.pathname + url.search);
        }
    }
    handleCartItemClick(e) {
        const target = e.target;
        if (target.matches("[data-cart-item-qty-decrease]") || target.closest("[data-cart-item-qty-decrease]")) {
            e.preventDefault();
            const button = target.matches("[data-cart-item-qty-decrease]") ? target : target.closest("[data-cart-item-qty-decrease]");
            const lineItemKey = button.dataset.cartItemQtyDecrease;
            const qtyInput = document.querySelector(`[data-cart-item-qty="${lineItemKey}"]`);
            if (qtyInput) {
                const currentQty = parseInt(qtyInput.value) || 1;
                if (currentQty > 1) {
                    qtyInput.value = currentQty - 1;
                    this.updateQuantity(lineItemKey, currentQty - 1);
                } else {
                    this.removeItem(lineItemKey);
                }
            }
        }
        if (target.matches("[data-cart-item-qty-increase]") || target.closest("[data-cart-item-qty-increase]")) {
            e.preventDefault();
            const button = target.matches("[data-cart-item-qty-increase]") ? target : target.closest("[data-cart-item-qty-increase]");
            const lineItemKey = button.dataset.cartItemQtyIncrease;
            const qtyInput = document.querySelector(`[data-cart-item-qty="${lineItemKey}"]`);
            if (qtyInput) {
                const currentQty = parseInt(qtyInput.value) || 1;
                qtyInput.value = currentQty + 1;
                this.updateQuantity(lineItemKey, currentQty + 1);
            }
        }
        if (target.matches("[data-cart-item-remove]") || target.closest("[data-cart-item-remove]")) {
            e.preventDefault();
            const button = target.matches("[data-cart-item-remove]") ? target : target.closest("[data-cart-item-remove]");
            const lineItemKey = button.dataset.cartItemRemove;
            this.removeItem(lineItemKey);
        }
        if (target.matches("[data-toggle-config]") || target.closest("[data-toggle-config]")) {
            e.preventDefault();
            const button = target.matches("[data-toggle-config]") ? target : target.closest("[data-toggle-config]");
            const configId = button.dataset.toggleConfig;
            this.toggleConfigChildren(configId, button);
        }
        if (target.matches("[data-toggle-clip]") || target.closest("[data-toggle-clip]")) {
            e.preventDefault();
            const button = target.matches("[data-toggle-clip]") ? target : target.closest("[data-toggle-clip]");
            const clipKey = button.dataset.toggleClip;
            this.toggleClipChildren(clipKey, button);
        }
        if (target.matches("[data-remove-group]") || target.closest("[data-remove-group]")) {
            e.preventDefault();
            const button = target.matches("[data-remove-group]") ? target : target.closest("[data-remove-group]");
            const configId = button.dataset.removeGroup;
            this.removeConfigurationGroup(configId);
        }
        if (target.matches("[data-cart-upsell-add]") || target.closest("[data-cart-upsell-add]")) {
            e.preventDefault();
            const button = target.matches("[data-cart-upsell-add]") ? target : target.closest("[data-cart-upsell-add]");
            const variantId = button.dataset.cartUpsellAdd;
            this.addUpsellProduct(variantId);
        }
        if (target.matches("[data-remove-discount]") || target.closest("[data-remove-discount]")) {
            e.preventDefault();
            const button = target.matches("[data-remove-discount]") ? target : target.closest("[data-remove-discount]");
            const discountCode = button.dataset.removeDiscount;
            this.removeDiscount(discountCode);
        }
    }
    handleCartItemChange(e) {
        const target = e.target;
        if (target.matches("[data-cart-item-qty]")) {
            const lineItemKey = target.dataset.cartItemQty;
            const newQuantity = parseInt(target.value) || 0;
            if (this.updateDebounce) {
                clearTimeout(this.updateDebounce);
            }
            this.updateDebounce = setTimeout(() => {
                this.updateQuantity(lineItemKey, newQuantity);
            }, 300);
        }
    }
    async updateQuantity(lineItemKey, newQuantity) {
        if (this.isUpdating) return;
        this.isUpdating = true;
        this.showLoadingState();
        try {
            if (newQuantity === 0) {
                const itemElement = document.querySelector(`[data-cart-item="${lineItemKey}"]`);
                if (itemElement) {
                    await gsap.effects.fadeOut(itemElement, {
                        duration: .3
                    });
                }
            }
            await window.cartStateManager.updateQuantity(lineItemKey, newQuantity);
        } catch (error) {
            console.error("Error updating quantity:", error);
        } finally {
            this.isUpdating = false;
            this.hideLoadingState();
        }
    }
    selectLinesToRemove(cart, lineItemKey, removedItem) {
        const removedProps = removedItem?.properties || {};
        const isClipLine = item => /clip/i.test(item.properties?.["_Item Type"] || "");
        const groupId = removedProps["_Configuration ID"] || (isClipLine(removedItem || {}) ? removedProps["_Parent Config ID"] : undefined);
        if (!groupId || !cart) return {
            groupId: undefined,
            isPartial: false,
            items: []
        };
        const inGroup = item => item.properties?.["_Configuration ID"] === groupId || item.properties?.["_Parent Config ID"] === groupId;
        const removedSeq = removedProps["_Clip Sequence"];
        const otherClipsInGroup = cart.items.filter(item => item.key !== lineItemKey && inGroup(item) && isClipLine(item));
        const isPartial = !!removedSeq && otherClipsInGroup.length > 0;
        const isFrameLensCharge = item => {
            const itemType = (item.properties?.["_Item Type"] || "").toLowerCase();
            return itemType.includes("prescription lens") && itemType.includes("child");
        };
        const items = isPartial ? cart.items.filter(item => inGroup(item) && item.properties?.["_Clip Sequence"] === removedSeq && !isFrameLensCharge(item)) : cart.items.filter(inGroup);
        return {
            groupId: groupId,
            isPartial: isPartial,
            items: items
        };
    }
    async removeItem(lineItemKey) {
        console.log("[Cart Drawer] ═══════════════════════════════════════════════");
        console.log("[Cart Drawer] removeItem called with key:", lineItemKey);
        let removedItem = null;
        let cart = null;
        try {
            console.log("[Cart Drawer] Fetching cart to capture item data...");
            const cartResponse = await fetch("/cart.js", {
                headers: {
                    Accept: "application/json"
                },
                credentials: "same-origin",
                cache: "no-store"
            });
            if (cartResponse.ok) {
                cart = await cartResponse.json();
                console.log(`[Cart Drawer] Cart has ${cart.items.length} items`);
                console.table?.(cart.items.map(item => ({
                    title: item.product_title,
                    seq: item.properties?.["_Clip Sequence"] ?? "—",
                    type: item.properties?.["_Item Type"] ?? "—",
                    config: item.properties?.["_Configuration ID"] ?? "",
                    parent: item.properties?.["_Parent Config ID"] ?? "",
                    key: item.key
                })));
                removedItem = cart.items.find(item => item.key === lineItemKey);
                if (removedItem) {
                    console.log("[Cart Drawer] ✓ Captured item data:", {
                        title: removedItem.product_title,
                        variantId: removedItem.variant_id,
                        itemType: removedItem.properties?.["_Item Type"],
                        configId: removedItem.properties?.["_Configuration ID"],
                        clipSequence: removedItem.properties?.["_Clip Sequence"]
                    });
                } else {
                    console.warn("[Cart Drawer] ⚠️ Could not find item with key:", lineItemKey);
                }
            }
        } catch (error) {
            console.warn("[Cart Drawer] Could not capture item data for validation:", error);
        }
        const {groupId: removedGroupId, isPartial: isPartialRemoval, items: allItemsToRemove} = this.selectLinesToRemove(cart, lineItemKey, removedItem);
        if (removedGroupId && cart) {
            console.log(`[Cart Drawer] ${isPartialRemoval ? `Clip #${removedItem?.properties?.["_Clip Sequence"]} of a multi-clip group — removing ${allItemsToRemove.length} item(s), leaving the rest` : `Whole configuration — removing ${allItemsToRemove.length} item(s) (parent + children)`}`);
            const fadeTargets = (isPartialRemoval ? allItemsToRemove.map(item => document.querySelector(`[data-cart-item="${item.key}"]`)) : [ document.querySelector(`[data-config-id="${removedGroupId}"]`) ]).filter(Boolean);
            if (fadeTargets.length > 0) {
                fadeTargets.forEach(el => el.classList.add("is-removing"));
                await gsap.to(fadeTargets, {
                    opacity: 0,
                    height: 0,
                    duration: .3,
                    ease: "power2.inOut"
                });
            }
            const orderedForRemoval = [ ...allItemsToRemove ].sort((a, b) => {
                const aIsParent = !!a.properties?.["_Configuration ID"] ? 1 : 0;
                const bIsParent = !!b.properties?.["_Configuration ID"] ? 1 : 0;
                return aIsParent - bIsParent;
            });
            const failed = [];
            let cascaded = 0;
            for (const item of orderedForRemoval) {
                console.log(`[Cart Drawer] Removing: ${item.product_title} (${item.variant_title || "parent"})`);
                const removeResponse = await fetch("/cart/change.js", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json"
                    },
                    credentials: "same-origin",
                    body: JSON.stringify({
                        id: item.key,
                        quantity: 0
                    })
                });
                if (!removeResponse.ok) {
                    const detail = await removeResponse.text().catch(() => "");
                    if (detail.indexOf("no valid id or line parameter") !== -1) {
                        cascaded++;
                        console.log(`[Cart Drawer] ${item.product_title} was already removed (cascaded with its parent)`);
                    } else {
                        failed.push({
                            title: item.product_title,
                            status: removeResponse.status,
                            detail: detail.slice(0, 200)
                        });
                        console.error(`[Cart Drawer] Failed to remove ${item.product_title} — ${removeResponse.status}`, detail.slice(0, 200));
                    }
                }
            }
            if (failed.length > 0) {
                console.error(`[Cart Drawer] ⚠️ ${failed.length} of ${orderedForRemoval.length} removals FAILED`, failed);
            } else {
                console.log(`[Cart Drawer] ✓ All items removed via batch${cascaded > 0 ? ` (${cascaded} cascaded)` : ""}`);
            }
            await window.cartStateManager.refresh();
        } else {
            const itemElement = document.querySelector(`[data-cart-item="${lineItemKey}"]`);
            if (itemElement) {
                itemElement.classList.add("is-removing");
            }
            console.log("[Cart Drawer] Performing removal via updateQuantity...");
            await this.updateQuantity(lineItemKey, 0);
            console.log("[Cart Drawer] ✓ updateQuantity completed");
        }
        if (removedItem) {
            console.log("[Cart Drawer] Starting post-removal validation...");
            try {
                await new Promise(resolve => setTimeout(resolve, 100));
                console.log("[Cart Drawer] Fetching updated cart for validation...");
                const updatedCartResponse = await fetch("/cart.js", {
                    headers: {
                        Accept: "application/json"
                    },
                    credentials: "same-origin",
                    cache: "no-store"
                });
                if (updatedCartResponse.ok) {
                    const updatedCart = await updatedCartResponse.json();
                    console.log(`[Cart Drawer] Updated cart has ${updatedCart.items.length} items`);
                    const orphanedClips = await this.validateDiscountEligibility(removedItem, updatedCart);
                    if (orphanedClips.length > 0) {
                        console.log("[Cart Drawer] Found orphaned clips, swapping to full price...");
                        await this.handleOrphanedDiscountedClips(orphanedClips, removedItem);
                    } else {
                        console.log("[Cart Drawer] No orphaned discounted clips found");
                    }
                }
            } catch (error) {
                console.error("[Cart Drawer] Error during discount validation:", error);
            }
        } else {
            console.log("[Cart Drawer] No item data captured, skipping validation");
        }
        console.log("[Cart Drawer] ═══════════════════════════════════════════════");
    }
    toggleConfigChildren(configId, button) {
        const parentGroup = button.closest("[data-config-id]");
        const childrenContainer = parentGroup ? parentGroup.querySelector(`[data-children-for="${configId}"]`) : document.querySelector(`[data-children-for="${configId}"]`);
        const chevron = button.querySelector(".cart-item__toggle-chevron");
        const isExpanded = button.getAttribute("aria-expanded") === "true";
        if (!childrenContainer) {
            console.warn(`[Cart Drawer] Children container not found for config ${configId}`, {
                parentGroup: parentGroup,
                configId: configId
            });
            return;
        }
        if (isExpanded) {
            gsap.to(childrenContainer, {
                height: 0,
                opacity: 0,
                duration: .3,
                ease: "power2.inOut",
                onComplete: () => {
                    childrenContainer.style.display = "none";
                }
            });
            if (chevron) {
                gsap.to(chevron, {
                    rotation: 0,
                    duration: .3,
                    ease: "power2.inOut"
                });
            }
            button.setAttribute("aria-expanded", "false");
            button.classList.remove("is-expanded");
        } else {
            childrenContainer.style.display = "block";
            const naturalHeight = childrenContainer.scrollHeight;
            gsap.fromTo(childrenContainer, {
                height: 0,
                opacity: 0
            }, {
                height: naturalHeight,
                opacity: 1,
                duration: .3,
                ease: "power2.inOut",
                onComplete: () => {
                    childrenContainer.style.height = "auto";
                }
            });
            if (chevron) {
                gsap.to(chevron, {
                    rotation: 180,
                    duration: .3,
                    ease: "power2.inOut"
                });
            }
            button.setAttribute("aria-expanded", "true");
            button.classList.add("is-expanded");
        }
    }
    toggleClipChildren(clipKey, button) {
        const childrenContainer = document.querySelector(`[data-children-for-clip="${clipKey}"]`);
        const chevron = button.querySelector(".cart-item__toggle-chevron");
        const isExpanded = button.getAttribute("aria-expanded") === "true";
        if (!childrenContainer) {
            console.warn(`[Cart Drawer] Children container not found for clip ${clipKey}`);
            return;
        }
        if (isExpanded) {
            gsap.to(childrenContainer, {
                height: 0,
                opacity: 0,
                duration: .3,
                ease: "power2.inOut",
                onComplete: () => {
                    childrenContainer.style.display = "none";
                }
            });
            if (chevron) {
                gsap.to(chevron, {
                    rotation: 0,
                    duration: .3,
                    ease: "power2.inOut"
                });
            }
            button.setAttribute("aria-expanded", "false");
            button.classList.remove("is-expanded");
        } else {
            childrenContainer.style.display = "block";
            const naturalHeight = childrenContainer.scrollHeight;
            gsap.fromTo(childrenContainer, {
                height: 0,
                opacity: 0
            }, {
                height: naturalHeight,
                opacity: 1,
                duration: .3,
                ease: "power2.inOut",
                onComplete: () => {
                    childrenContainer.style.height = "auto";
                }
            });
            if (chevron) {
                gsap.to(chevron, {
                    rotation: 180,
                    duration: .3,
                    ease: "power2.inOut"
                });
            }
            button.setAttribute("aria-expanded", "true");
            button.classList.add("is-expanded");
        }
    }
    async removeConfigurationGroup(configId) {
        if (this.isUpdating) {
            return;
        }
        this.isUpdating = true;
        this.showLoadingState();
        try {
            const cartResponse = await fetch("/cart.js", {
                headers: {
                    Accept: "application/json"
                },
                credentials: "same-origin"
            });
            if (!cartResponse.ok) {
                throw new Error("Failed to fetch cart");
            }
            const cart = await cartResponse.json();
            const itemsToRemove = cart.items.filter(item => {
                const hasMatchingConfigId = item.properties && item.properties["_Configuration ID"] === configId;
                const hasMatchingParentId = item.properties && item.properties["_Parent Config ID"] === configId;
                return hasMatchingConfigId || hasMatchingParentId;
            });
            if (itemsToRemove.length === 0) {
                console.warn(`[Cart Drawer] No items found for config ${configId}`);
                return;
            }
            const groupElement = document.querySelector(`[data-config-id="${configId}"]`);
            if (groupElement) {
                groupElement.classList.add("is-removing");
                await gsap.to(groupElement, {
                    opacity: 0,
                    height: 0,
                    duration: .3,
                    ease: "power2.inOut"
                });
            }
            for (const item of itemsToRemove) {
                const itemTitle = item.product_title || item.title || "Unknown item";
                const removeResponse = await fetch("/cart/change.js", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json"
                    },
                    credentials: "same-origin",
                    body: JSON.stringify({
                        id: item.key,
                        quantity: 0
                    })
                });
                if (!removeResponse.ok) {
                    console.error(`[Cart Drawer] Failed to remove item ${item.key}`);
                }
            }
            await window.cartStateManager.refresh();
            this.showSuccess(`Removed configuration (${itemsToRemove.length} items)`);
        } catch (error) {
            console.error("[Cart Drawer] Error removing configuration group:", error);
            this.showError("Failed to remove configuration. Please try again.");
        } finally {
            this.isUpdating = false;
            this.hideLoadingState();
        }
    }
    async addUpsellProduct(variantId) {
        if (this.isUpdating) return;
        this.isUpdating = true;
        this.showLoadingState();
        try {
            await window.cartStateManager.add({
                id: variantId,
                quantity: 1
            });
        } catch (error) {
            console.error("Error adding product:", error);
        } finally {
            this.isUpdating = false;
            this.hideLoadingState();
        }
    }
    async removeDiscount(discountCode) {
        if (this.isUpdating) return;
        this.isUpdating = true;
        this.showLoadingState();
        try {
            const discountElement = document.querySelector(`[data-remove-discount="${discountCode}"]`)?.closest(".cart-drawer__discount");
            if (discountElement) {
                await gsap.to(discountElement, {
                    opacity: 0,
                    height: 0,
                    duration: .3,
                    ease: "power2.inOut"
                });
            }
            const response = await fetch("/cart/update.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json"
                },
                credentials: "same-origin",
                body: JSON.stringify({
                    discount: ""
                })
            });
            if (!response.ok) {
                throw new Error("Failed to remove discount");
            }
            await window.cartStateManager.refresh();
            this.showSuccess("Discount removed");
        } catch (error) {
            console.error("Error removing discount:", error);
            this.showError("Failed to remove discount. Please try again.");
        } finally {
            this.isUpdating = false;
            this.hideLoadingState();
        }
    }
    async handleAddToCart(data) {
        if (this.isUpdating) return;
        this.isUpdating = true;
        this.showLoadingState();
        try {
            await window.cartStateManager.add(data);
        } catch (error) {
            console.error("Error adding to cart:", error);
        } finally {
            this.isUpdating = false;
            this.hideLoadingState();
        }
    }
    async refreshCartHTML() {
        try {
            const sectionResponse = await fetch("/?section_id=cart-drawer-items");
            if (sectionResponse.ok) {
                const html = await sectionResponse.text();
                const parser = new DOMParser;
                const doc = parser.parseFromString(html, "text/html");
                const shopifySection = doc.querySelector(".shopify-section");
                const sectionContent = shopifySection ? shopifySection.innerHTML : doc.body.innerHTML;
                if (this.cartItemsContainer && sectionContent) {
                    this.cartItemsContainer.innerHTML = sectionContent;
                    this.cartTotal = document.querySelectorAll("[data-cart-total]");
                    if (this.cartTotal.length === 0) {
                        console.warn("[Cart Drawer] ⚠️ No [data-cart-total] elements found after section replacement");
                        console.warn("[Cart Drawer] This means the cart summary section is missing from cart-drawer-items.liquid");
                    }
                    const cartData = window.cartStateManager?.getState();
                    if (cartData && this.cartTotal.length > 0) {
                        const formattedTotal = "$" + (cartData.total_price / 100).toFixed(2);
                        this.cartTotal.forEach(element => {
                            element.textContent = formattedTotal;
                        });
                    }
                    let groupedCount = 0;
                    const cartItemGroups = this.cartItemsContainer.querySelectorAll(".cart-item-group");
                    groupedCount = cartItemGroups.length;
                    const standaloneItems = this.cartItemsContainer.querySelectorAll(".cart-drawer__items > .cart-item");
                    groupedCount += standaloneItems.length;
                    const drawerCount = document.querySelector(".cart-drawer__count[data-cart-count]");
                    if (drawerCount) {
                        drawerCount.textContent = `(${groupedCount})`;
                    }
                    const headerBadge = document.querySelector(".chemistrie-header__cart-count[data-cart-count]");
                    if (headerBadge) {
                        headerBadge.textContent = groupedCount;
                        headerBadge.style.display = groupedCount === 0 ? "none" : "";
                    }
                    this.validateCartIntegrity();
                } else {
                    console.error("[Cart Drawer] ❌ Failed to replace content:", {
                        containerExists: !!this.cartItemsContainer,
                        contentLength: sectionContent.length
                    });
                }
            } else {
                console.error("[Cart Drawer] ❌ Section fetch failed:", sectionResponse.status);
            }
            const headerResponse = await fetch("/?section_id=cart-header-dropdown");
            if (headerResponse.ok) {
                const headerHtml = await headerResponse.text();
                const headerParser = new DOMParser;
                const headerDoc = headerParser.parseFromString(headerHtml, "text/html");
                const shopifyHeaderSection = headerDoc.querySelector(".shopify-section");
                const headerContent = shopifyHeaderSection ? shopifyHeaderSection.innerHTML : headerDoc.body.innerHTML;
                const headerContainer = document.querySelector('[data-section="cart-header-dropdown"]');
                if (headerContainer && headerContent) {
                    headerContainer.innerHTML = headerContent;
                }
            }
        } catch (error) {
            console.error("Error refreshing cart HTML:", error);
            this.showError("Failed to load cart. Please refresh the page.");
        }
    }
    async refreshCart() {
        try {
            await window.cartStateManager.refresh();
        } catch (error) {
            console.error("Error refreshing cart:", error);
            this.showError("Failed to load cart. Please refresh the page.");
        }
    }
    updateCartUI(cart) {
        this.cartCount.forEach(element => {
            element.textContent = cart.item_count;
            element.style.display = cart.item_count > 0 ? "" : "none";
        });
        this.cartTotal.forEach(element => {
            element.textContent = this.formatMoney(cart.total_price);
        });
        if (this.cartEmpty && this.cartContent) {
            if (cart.item_count === 0) {
                this.cartContent.style.display = "none";
                this.cartEmpty.style.display = "block";
            } else {
                this.cartContent.style.display = "block";
                this.cartEmpty.style.display = "none";
            }
        }
    }
    formatMoney(cents) {
        return "$" + (cents / 100).toFixed(2);
    }
    showLoadingState() {
        this.drawer?.classList.add("is-loading");
        const inputs = this.cartItemsContainer?.querySelectorAll("input, button");
        inputs?.forEach(input => input.disabled = true);
    }
    hideLoadingState() {
        this.drawer?.classList.remove("is-loading");
        const inputs = this.cartItemsContainer?.querySelectorAll("input, button");
        inputs?.forEach(input => input.disabled = false);
    }
    showSuccess(message) {
        this.showNotification(message, "success");
    }
    showError(message) {
        this.showNotification(message, "error");
    }
    showNotification(message, type = "info") {
        const notification = document.createElement("div");
        notification.className = `cart-notification cart-notification--${type}`;
        notification.textContent = message;
        notification.setAttribute("role", "alert");
        notification.setAttribute("aria-live", "polite");
        const container = this.drawer || document.body;
        container.appendChild(notification);
        gsap.fromTo(notification, {
            opacity: 0,
            y: -20
        }, {
            opacity: 1,
            y: 0,
            duration: .3,
            ease: "power2.out"
        });
        setTimeout(() => {
            gsap.to(notification, {
                opacity: 0,
                y: -20,
                duration: .3,
                ease: "power2.in",
                onComplete: () => notification.remove()
            });
        }, 3e3);
    }
    trapFocus() {
        const focusableElements = this.drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusableElements.length === 0) return;
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];
        this.focusTrap = e => {
            if (e.key !== "Tab") return;
            if (e.shiftKey) {
                if (document.activeElement === firstFocusable) {
                    e.preventDefault();
                    lastFocusable.focus();
                }
            } else {
                if (document.activeElement === lastFocusable) {
                    e.preventDefault();
                    firstFocusable.focus();
                }
            }
        };
        this.drawer.addEventListener("keydown", this.focusTrap);
    }
    releaseFocus() {
        if (this.focusTrap) {
            this.drawer.removeEventListener("keydown", this.focusTrap);
            this.focusTrap = null;
        }
    }
    focusFirstElement() {
        const firstFocusable = this.drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
            firstFocusable.focus();
        }
    }
    updateCartSections(sections) {
        if (!sections) return;
        if (sections["cart-drawer-items"]) {
            const drawerContainer = document.querySelector('[data-section="cart-drawer-items"]');
            if (drawerContainer) {
                drawerContainer.innerHTML = sections["cart-drawer-items"];
            }
        }
        if (sections["cart-header-dropdown"]) {
            const dropdownContainer = document.querySelector('[data-section="cart-header-dropdown"]');
            if (dropdownContainer) {
                dropdownContainer.innerHTML = sections["cart-header-dropdown"];
            }
        }
        this.updateCartCount();
        document.dispatchEvent(new CustomEvent("cart:sections:updated", {
            detail: {
                sections: sections
            }
        }));
    }
    updateCartCount() {
        console.warn("[Cart Drawer] updateCartCount() is deprecated - handled by CartStateManager");
        if (window.cartStateManager) {
            window.cartStateManager.refresh();
        }
    }
    validateCartIntegrity() {
        if (!this.cartItemsContainer) return;
        const groups = this.cartItemsContainer.querySelectorAll(".cart-item-group[data-config-id]");
        let hasIntegrityIssue = false;
        groups.forEach(group => {
            const isClipOrder = group.dataset.isClipOrder === "true";
            if (!isClipOrder) return;
            const hasBridge = group.dataset.hasBridge === "true";
            const hasMagnet = group.dataset.hasMagnet === "true";
            if (!hasBridge || !hasMagnet) {
                hasIntegrityIssue = true;
                console.warn("[Cart Drawer] Integrity issue for config:", group.dataset.configId, "— Bridge:", hasBridge, "Magnet:", hasMagnet);
            }
        });
        const warningBanner = this.cartItemsContainer.querySelector(".cart-drawer__integrity-warning");
        const checkoutBtn = this.cartItemsContainer.querySelector("[data-checkout-blocked]");
        if (hasIntegrityIssue) {
            if (warningBanner) warningBanner.style.display = "";
            this.drawer?.classList.add("cart-drawer--integrity-warning");
            if (checkoutBtn) {
                checkoutBtn.classList.add("cart-drawer__checkout--disabled");
                checkoutBtn.removeAttribute("href");
                checkoutBtn.setAttribute("aria-disabled", "true");
            }
        } else {
            if (warningBanner) warningBanner.style.display = "none";
            this.drawer?.classList.remove("cart-drawer--integrity-warning");
            if (checkoutBtn && checkoutBtn.dataset.checkoutBlocked === "true") {}
        }
        this.reportOrphanedChildren();
    }
    async reportOrphanedChildren() {
        try {
            const cartResponse = await fetch("/cart.js", {
                headers: {
                    Accept: "application/json"
                },
                credentials: "same-origin",
                cache: "no-store"
            });
            if (!cartResponse.ok) return;
            const cart = await cartResponse.json();
            const parentConfigIds = new Set;
            cart.items.forEach(item => {
                const configId = item.properties?.["_Configuration ID"];
                const parentId = item.properties?.["_Parent Config ID"];
                if (configId && !parentId) {
                    parentConfigIds.add(configId);
                }
            });
            const orphans = cart.items.filter(item => {
                const parentId = item.properties?.["_Parent Config ID"];
                return parentId && !parentConfigIds.has(parentId);
            });
            const CHILD_ONLY_HANDLES = [ "lens-upgrade-addon", "crystal-addon" ];
            const fraudulentItems = cart.items.filter(item => {
                if (!CHILD_ONLY_HANDLES.includes(item.handle)) return false;
                if (item.properties?.["_Parent Config ID"]) return false;
                const itemType = item.properties?.["_Item Type"] || "";
                const isRxLensParent = item.properties?.["_Configuration ID"] && item.properties?.["_Order Type"] === "New Rx Lenses" && itemType.indexOf("Prescription Lenses (Parent)") !== -1;
                if (isRxLensParent) return false;
                return true;
            });
            if (fraudulentItems.length > 0) {
                console.warn("[Cart Drawer] Found", fraudulentItems.length, "standalone child-only product(s) with no configurator context");
                orphans.push(...fraudulentItems);
            }
            if (orphans.length > 0) {
                console.warn(`[Cart Drawer] ⚠️ ${orphans.length} orphaned child line(s) — NOT removed, reporting only.`, orphans.map(o => ({
                    title: o.product_title,
                    parent: o.properties?.["_Parent Config ID"] || "(none)",
                    seq: o.properties?.["_Clip Sequence"] || "—",
                    key: o.key
                })));
            }
        } catch (error) {
            console.error("[Cart Drawer] Error during orphan report:", error);
        }
    }
    getDiscountedVariantMap() {
        const FALLBACK_DISCOUNTED_VARIANTS = {
            45363488063649: "sun_clips",
            45363487965345: "reader_clips",
            45363487998113: "blue_light_clips",
            45363487932577: "clips_3d",
            45363488096417: "fl41_therapeutic_clips",
            45363488030881: "driving_clips",
            45694416355489: "light_sensitivity_clips"
        };
        const products = window.chemistrie_products;
        if (products) {
            const map = {};
            const clipTypes = [ "sun_clips", "reader_clips", "blue_light_clips", "clips_3d", "fl41_therapeutic_clips", "driving_clips", "light_sensitivity_clips" ];
            clipTypes.forEach(key => {
                if (products[key]?.discounted_variant_id) {
                    map[products[key].discounted_variant_id] = key;
                }
            });
            console.log("[Discount Validation] Using dynamic variant map from chemistrie_products");
            return map;
        }
        console.log("[Discount Validation] Using hardcoded fallback variant map");
        return FALLBACK_DISCOUNTED_VARIANTS;
    }
    isDiscountedVariant(item) {
        const discountMap = this.getDiscountedVariantMap();
        return !!discountMap[item.variant_id];
    }
    getProductKeyForVariant(variantId) {
        const discountMap = this.getDiscountedVariantMap();
        return discountMap[variantId] || null;
    }
    getProductHandle(productKey) {
        const handleMap = {
            sun_clips: "clip-on-sun-lenses",
            reader_clips: "clip-on-reader-lenses",
            blue_light_clips: "clip-on-blue-light-lenses",
            clips_3d: "clip-on-3d-lenses",
            fl41_therapeutic_clips: "fl41-therapeutic-clips",
            driving_clips: "clip-on-night-driving-lenses",
            light_sensitivity_clips: "clip-on-light-sensitivity-lenses"
        };
        return handleMap[productKey] || null;
    }
    async findFullPriceVariant(item) {
        const productKey = this.getProductKeyForVariant(item.variant_id);
        if (!productKey) {
            console.error("[Discount Validation] Could not determine product key for variant:", item.variant_id);
            return null;
        }
        console.log(`[Discount Validation] Finding full-price variant for ${productKey}`);
        const lensCategory = item.properties?.["_Lens Category"] || "";
        const lensColor = item.properties?.["_Lens Color"] || "";
        const magnification = item.properties?.["_Magnification"] || "";
        const therapeuticType = item.properties?.["_Therapeutic Type"] || "";
        let expectedTitle = "";
        if (productKey === "sun_clips") {
            expectedTitle = `${lensCategory} ${lensColor}`.trim();
        } else if (productKey === "reader_clips") {
            expectedTitle = magnification || "Default Title";
        } else if (productKey === "fl41_therapeutic_clips") {
            expectedTitle = therapeuticType || "FL-41 Light";
        } else if (productKey === "light_sensitivity_clips") {
            expectedTitle = `FL-41 ${item.properties?.["_FL-41 Intensity"] || "Light"}`;
        } else {
            expectedTitle = "Default Title";
        }
        console.log(`[Discount Validation] Looking for variant: "${expectedTitle}" in ${productKey}`);
        const products = window.chemistrie_products;
        let variants = products?.[productKey]?.variants;
        if (!variants || !variants.length) {
            console.log("[Discount Validation] Fetching product data from Shopify API...");
            const handle = this.getProductHandle(productKey);
            if (!handle) {
                console.error("[Discount Validation] No handle found for product key:", productKey);
                return null;
            }
            try {
                const response = await fetch(`/products/${handle}.js`);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const productData = await response.json();
                variants = productData.variants;
                console.log(`[Discount Validation] Fetched ${variants.length} variants from API`);
            } catch (error) {
                console.error("[Discount Validation] Failed to fetch product:", error);
                return null;
            }
        }
        if (!variants || !variants.length) {
            console.error("[Discount Validation] No variants found for product");
            return null;
        }
        const discountedVariantId = item.variant_id;
        let matchedVariant = variants.find(v => v.id !== discountedVariantId && v.title.toLowerCase() === expectedTitle.toLowerCase());
        if (!matchedVariant) {
            matchedVariant = variants.find(v => v.id !== discountedVariantId && (v.title.toLowerCase().includes(lensColor.toLowerCase()) || v.title.toLowerCase().includes(lensCategory.toLowerCase())));
        }
        if (!matchedVariant) {
            matchedVariant = variants.find(v => v.id !== discountedVariantId);
            if (matchedVariant) {
                console.warn(`[Discount Validation] Using first available variant: ${matchedVariant.title}`);
            }
        }
        if (!matchedVariant) {
            console.error("[Discount Validation] Could not find any full-price variant");
            return null;
        }
        console.log(`[Discount Validation] Found full-price variant: ${matchedVariant.title} (ID: ${matchedVariant.id})`);
        return matchedVariant.id;
    }
    async validateDiscountEligibility(removedItem, cart) {
        console.log("[Discount Validation] ═══════════════════════════════════════════");
        console.log("[Discount Validation] Checking eligibility after item removal");
        console.log("[Discount Validation] Removed item:", {
            title: removedItem?.product_title,
            variantId: removedItem?.variant_id,
            properties: removedItem?.properties
        });
        const removedProps = removedItem?.properties || {};
        const removedItemType = removedProps["_Item Type"] || "";
        const removedIsClip = /clip/i.test(removedItemType);
        const removedGroupId = removedProps["_Configuration ID"] || removedProps["_Parent Config ID"];
        console.log("[Discount Validation] Removed-clip check:", {
            removedItemType: removedItemType,
            removedIsClip: removedIsClip,
            removedGroupId: removedGroupId,
            removedClipSequence: removedProps["_Clip Sequence"]
        });
        if (!removedIsClip || !removedGroupId) {
            console.log("[Discount Validation] ❌ Not a grouped clip removal, skipping validation");
            return [];
        }
        const inGroup = item => item.properties?.["_Configuration ID"] === removedGroupId || item.properties?.["_Parent Config ID"] === removedGroupId;
        const groupClips = cart.items.filter(item => inGroup(item) && /clip/i.test(item.properties?.["_Item Type"] || ""));
        const anchoredByFullPriceClip = groupClips.some(item => !this.isDiscountedVariant(item));
        if (groupClips.length === 0 || anchoredByFullPriceClip) {
            console.log("[Discount Validation] ✓ Nothing stranded", {
                clipsLeftInGroup: groupClips.length,
                anchoredByFullPriceClip: anchoredByFullPriceClip
            });
            console.log("[Discount Validation] ═══════════════════════════════════════════");
            return [];
        }
        const orphanedClips = groupClips.filter(item => this.isDiscountedVariant(item));
        console.log(`[Discount Validation] Found ${orphanedClips.length} stranded discounted clip(s) in group ${removedGroupId}`);
        console.log("[Discount Validation] ═══════════════════════════════════════════");
        return orphanedClips;
    }
    async swapDiscountedToFullPrice(discountedItem, removedItem = null) {
        console.log("[Discount Validation] ═══ SWAP OPERATION START ═══");
        console.log(`[Discount Validation] Swapping: ${discountedItem.product_title}`);
        console.log("[Discount Validation] Item key:", discountedItem.key);
        console.log("[Discount Validation] Current variant ID:", discountedItem.variant_id);
        console.log("[Discount Validation] Properties:", discountedItem.properties);
        const fullPriceVariantId = await this.findFullPriceVariant(discountedItem);
        if (!fullPriceVariantId) {
            console.error("[Discount Validation] ❌ Could not find full-price variant");
            return false;
        }
        console.log(`[Discount Validation] ✓ Target full-price variant ID: ${fullPriceVariantId}`);
        const groupId = discountedItem.properties?.["_Configuration ID"] || discountedItem.properties?.["_Parent Config ID"];
        const oldSequence = discountedItem.properties?.["_Clip Sequence"];
        const childrenToRehome = await this.getClipChildren(groupId, oldSequence);
        console.log(`[Discount Validation] ${childrenToRehome.length} child line(s) will be re-homed onto the replacement`);
        const frameParent = await this.getGroupFrameParent(groupId);
        if (frameParent) {
            console.log("[Discount Validation] Group is frame-owned (Variant C) — the promoted clip will become its child");
        }
        let addedKey = null;
        try {
            let updatedProperties = {
                ...discountedItem.properties
            };
            updatedProperties["_Clip Sequence"] = "1";
            updatedProperties["_Price Adjusted"] = "Discount removed - primary clip was deleted";
            const moneyInputs = await this.promotionMoneyInputs(discountedItem, fullPriceVariantId, groupId, frameParent);
            if (moneyInputs) {
                updatedProperties = this.promotedClipMoneyProps(updatedProperties, moneyInputs);
            } else {
                console.warn("[Discount Validation] Could not price the promotion — money keys left as they were");
            }
            updatedProperties = this.promotedClipRxProps(updatedProperties, removedItem && removedItem.properties);
            if (frameParent) {
                delete updatedProperties["_Configuration ID"];
                updatedProperties["_Parent Config ID"] = groupId;
                updatedProperties["_Item Type"] = "  ↳ Clip (Child)";
            }
            console.log("[Discount Validation] Step 1: Adding full-price variant...");
            console.log("[Discount Validation] New properties:", updatedProperties);
            const addResponse = await fetch("/cart/add.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json"
                },
                credentials: "same-origin",
                body: JSON.stringify({
                    id: fullPriceVariantId,
                    quantity: discountedItem.quantity,
                    properties: updatedProperties,
                    ...frameParent ? {
                        parent_line_key: frameParent.key
                    } : {}
                })
            });
            if (!addResponse.ok) {
                const errorText = await addResponse.text();
                console.error("[Discount Validation] Add failed:", addResponse.status, errorText);
                throw new Error("Failed to add full-price variant");
            }
            const addResult = await addResponse.json();
            addedKey = addResult.key;
            console.log("[Discount Validation] ✓ Add successful:", addResult.title, "at $" + addResult.price / 100);
            console.log("[Discount Validation] Step 2: Removing discounted item...");
            const removeResponse = await fetch("/cart/change.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json"
                },
                credentials: "same-origin",
                body: JSON.stringify({
                    id: discountedItem.key,
                    quantity: 0
                })
            });
            if (!removeResponse.ok) {
                const errorText = await removeResponse.text();
                console.error("[Discount Validation] Remove failed:", removeResponse.status, errorText);
                if (addedKey) {
                    console.warn("[Discount Validation] Unwinding the full-price line to avoid a double charge");
                    await fetch("/cart/change.js", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json"
                        },
                        credentials: "same-origin",
                        body: JSON.stringify({
                            id: addedKey,
                            quantity: 0
                        })
                    }).catch(() => {});
                }
                throw new Error("Failed to remove discounted item");
            }
            const removeResult = await removeResponse.json();
            console.log("[Discount Validation] ✓ Remove successful, cart now has", removeResult.item_count, "items");
            if (childrenToRehome.length > 0) {
                await this.rehomeClipChildren(childrenToRehome, frameParent ? frameParent.key : addedKey, "1");
            }
            console.log("[Discount Validation] ═══ SWAP OPERATION COMPLETE ═══");
            return true;
        } catch (error) {
            console.error("[Discount Validation] ❌ Error swapping variant:", error);
            return false;
        }
    }
    promotedClipRxProps(props, removedProps) {
        const out = Object.assign({}, props || {});
        const from = removedProps || {};
        const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
        if (!has(from, "_Prescription Status") && !has(from, "_Rx Upload Token")) return out;
        [ "_Prescription Status", "_Rx Upload Token", "_Rx Upload Filename", "_Rx Certified At" ].forEach(k => {
            if (has(from, k)) out[k] = from[k]; else delete out[k];
        });
        return out;
    }
    async promotionMoneyInputs(discountedItem, fullPriceVariantId, groupId, frameParent) {
        try {
            const productKey = this.getProductKeyForVariant(discountedItem.variant_id);
            let variants = window.chemistrie_products?.[productKey]?.variants;
            if (!variants || !variants.length) {
                const handle = this.getProductHandle(productKey);
                if (!handle) return null;
                const response = await fetch(`/products/${handle}.js`);
                if (!response.ok) return null;
                variants = (await response.json()).variants;
            }
            const full = (variants || []).find(v => String(v.id) === String(fullPriceVariantId));
            if (!full) return null;
            const cartResponse = await fetch("/cart.js", {
                credentials: "same-origin",
                cache: "no-store"
            });
            if (!cartResponse.ok) return null;
            const cart = await cartResponse.json();
            const lensCents = cart.items.filter(line => {
                const p = line.properties || {};
                return (p["_Parent Config ID"] === groupId || p["_Configuration ID"] === groupId) && /prescription lens/i.test(p["_Item Type"] || "");
            }).reduce((sum, line) => sum + line.price, 0);
            return {
                discountedCents: discountedItem.price,
                fullCents: full.price,
                lensCents: lensCents,
                frameCents: frameParent ? frameParent.price : 0
            };
        } catch (error) {
            console.warn("[Discount Validation] Pricing the promotion failed:", error);
            return null;
        }
    }
    promotedClipMoneyProps(props, inputs) {
        const out = Object.assign({}, props || {});
        const has = k => Object.prototype.hasOwnProperty.call(out, k);
        const money = cents => {
            const num = cents / 100;
            return num % 1 === 0 ? `$${num}` : `$${num.toFixed(2)}`;
        };
        const parse = v => Math.round(parseFloat(String(v || "").replace(/[^0-9.\-]/g, "")) * 100) || 0;
        const oldTotalCents = has("_Configuration Total") ? parse(out["_Configuration Total"]) : inputs.discountedCents;
        const totalCents = oldTotalCents - inputs.discountedCents + inputs.fullCents + inputs.lensCents + inputs.frameCents;
        out["_Configuration Total"] = money(totalCents);
        if (has("_Native Lens Upgrades Total")) out["_Native Lens Upgrades Total"] = money(inputs.lensCents);
        if (has("_Base Clip Price")) out["_Base Clip Price"] = money(inputs.fullCents);
        if (has("_Note")) {
            out["_Note"] = `Cart shows base variant price (${money(inputs.fullCents)}). Total with all customization: ${money(totalCents)}`;
        }
        if (has("_Clip Number")) out["_Clip Number"] = "1st";
        return out;
    }
    async getGroupFrameParent(groupId) {
        if (!groupId) return null;
        const response = await fetch("/cart.js", {
            headers: {
                Accept: "application/json"
            },
            credentials: "same-origin",
            cache: "no-store"
        });
        if (!response.ok) {
            console.error("[Discount Validation] Could not read cart to find the group frame parent");
            return null;
        }
        const cart = await response.json();
        return cart.items.find(item => {
            const props = item.properties || {};
            if (props["_Configuration ID"] !== groupId) return false;
            if (props["_Parent Config ID"]) return false;
            const itemType = props["_Item Type"] || "";
            if (/clip/i.test(itemType)) return false;
            return /frame/i.test(itemType);
        }) || null;
    }
    async getClipChildren(groupId, sequence) {
        if (!groupId || !sequence) return [];
        const response = await fetch("/cart.js", {
            headers: {
                Accept: "application/json"
            },
            credentials: "same-origin",
            cache: "no-store"
        });
        if (!response.ok) {
            console.error("[Discount Validation] Could not read cart to snapshot clip children");
            return [];
        }
        const cart = await response.json();
        return cart.items.filter(item => item.properties?.["_Parent Config ID"] === groupId && item.properties?.["_Clip Sequence"] === sequence);
    }
    async rehomeClipChildren(children, parentKey, sequence) {
        console.log(`[Discount Validation] Re-homing ${children.length} child line(s) onto ${parentKey} at sequence ${sequence}`);
        for (const child of children) {
            const response = await fetch("/cart/add.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json"
                },
                credentials: "same-origin",
                body: JSON.stringify({
                    id: child.variant_id,
                    quantity: child.quantity,
                    properties: {
                        ...child.properties,
                        "_Clip Sequence": sequence
                    },
                    ...parentKey ? {
                        parent_line_key: parentKey
                    } : {}
                })
            });
            if (!response.ok) {
                const detail = await response.text().catch(() => "");
                console.error(`[Discount Validation] Could not re-home ${child.product_title} — ${response.status}`, detail.slice(0, 200));
            } else {
                console.log(`[Discount Validation] ✓ Re-homed ${child.product_title}`);
            }
        }
    }
    async handleOrphanedDiscountedClips(orphanedClips, removedItem = null) {
        if (!orphanedClips.length) return;
        console.log(`[Discount Validation] Handling ${orphanedClips.length} orphaned clips`);
        const sortedClips = orphanedClips.sort((a, b) => {
            const seqA = parseInt(a.properties?.["_Clip Sequence"] || 999);
            const seqB = parseInt(b.properties?.["_Clip Sequence"] || 999);
            return seqA - seqB;
        });
        const clipToSwap = sortedClips[0];
        console.log(`[Discount Validation] Swapping clip with sequence ${clipToSwap.properties?.["_Clip Sequence"]} to full price`);
        console.log(`[Discount Validation] ${sortedClips.length - 1} other clip(s) will keep discount (new primary exists after swap)`);
        this.showNotification(`Price adjusted: One clip is now at full price ($175) as the original primary clip was removed.`, "info");
        const success = await this.swapDiscountedToFullPrice(clipToSwap, removedItem);
        if (!success) {
            console.error(`[Discount Validation] Failed to swap clip: ${clipToSwap.product_title}`);
        }
        await window.cartStateManager.refresh();
    }
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
        window.cartDrawer = new CartDrawer;
    });
} else {
    window.cartDrawer = new CartDrawer;
}

window.CartDrawer = CartDrawer;